import React, { useState, useEffect, useCallback } from 'react';
import { Plus, Trash2, Edit2, Save, X, GripVertical, FileText } from 'lucide-react';
import { CustomerCustomAttachment } from '../../../shared/types';
import { customerAttachmentService } from '../../../services/customer/CustomerAttachmentService';

interface CustomAttachmentsManagerProps {
  customerId: string;
  onUpdate: () => void;
}

export const CustomAttachmentsManager: React.FC<CustomAttachmentsManagerProps> = ({
  customerId,
  onUpdate
}) => {
  const [attachments, setAttachments] = useState<CustomerCustomAttachment[]>([]);
  const [loading, setLoading] = useState(true);
  const [editingId, setEditingId] = useState<string | null>(null);
  const [editForm, setEditForm] = useState<{
    filename: string;
    description: string;
    file_type: CustomerCustomAttachment['file_type'];
    is_required: boolean;
  }>({
    filename: '',
    description: '',
    file_type: 'pdf',
    is_required: true
  });
  const [isAdding, setIsAdding] = useState(false);
  const [newForm, setNewForm] = useState<{
    filename: string;
    description: string;
    file_type: CustomerCustomAttachment['file_type'];
    is_required: boolean;
  }>({
    filename: '',
    description: '',
    file_type: 'pdf',
    is_required: true
  });

  const loadAttachments = useCallback(async () => {
    setLoading(true);
    const data = await customerAttachmentService.getCustomerAttachments(customerId);
    setAttachments(data);
    setLoading(false);
  }, [customerId]);

  useEffect(() => {
    loadAttachments();
  }, [loadAttachments]);

  const handleAdd = async () => {
    if (!newForm.filename.trim()) return;

    const success = await customerAttachmentService.createAttachment(
      customerId,
      newForm.filename.trim(),
      newForm.file_type,
      newForm.description.trim(),
      newForm.is_required
    );

    if (success) {
      await loadAttachments();
      setNewForm({
        filename: '',
        description: '',
        file_type: 'pdf',
        is_required: true
      });
      setIsAdding(false);
      onUpdate();
    }
  };

  const handleEdit = (attachment: CustomerCustomAttachment) => {
    setEditingId(attachment.id);
    setEditForm({
      filename: attachment.filename,
      description: attachment.description || '',
      file_type: attachment.file_type,
      is_required: attachment.is_required
    });
  };

  const handleSaveEdit = async (attachmentId: string) => {
    const success = await customerAttachmentService.updateAttachment(attachmentId, {
      filename: editForm.filename,
      description: editForm.description || undefined,
      file_type: editForm.file_type,
      is_required: editForm.is_required
    });

    if (success) {
      await loadAttachments();
      setEditingId(null);
      onUpdate();
    }
  };

  const handleDelete = async (attachmentId: string) => {
    if (!window.confirm('Möchten Sie diesen Anhang wirklich löschen?')) return;

    const success = await customerAttachmentService.deleteAttachment(attachmentId);
    if (success) {
      await loadAttachments();
      onUpdate();
    }
  };

  const handleMoveUp = async (index: number) => {
    if (index === 0) return;
    const newOrder = [...attachments];
    [newOrder[index - 1], newOrder[index]] = [newOrder[index], newOrder[index - 1]];
    const success = await customerAttachmentService.reorderAttachments(
      customerId,
      newOrder.map(a => a.id)
    );
    if (success) {
      setAttachments(newOrder);
      onUpdate();
    }
  };

  const handleMoveDown = async (index: number) => {
    if (index === attachments.length - 1) return;
    const newOrder = [...attachments];
    [newOrder[index], newOrder[index + 1]] = [newOrder[index + 1], newOrder[index]];
    const success = await customerAttachmentService.reorderAttachments(
      customerId,
      newOrder.map(a => a.id)
    );
    if (success) {
      setAttachments(newOrder);
      onUpdate();
    }
  };

  const getFileTypeLabel = (type: CustomerCustomAttachment['file_type']): string => {
    const labels: Record<CustomerCustomAttachment['file_type'], string> = {
      pdf: 'PDF',
      doc: 'Word (.doc)',
      docx: 'Word (.docx)',
      xlsx: 'Excel (.xlsx)',
      xls: 'Excel (.xls)',
      txt: 'Text',
      jpg: 'JPG',
      jpeg: 'JPEG',
      png: 'PNG',
      xml: 'XML',
      zip: 'ZIP-Archiv'
    };
    return labels[type] || type.toUpperCase();
  };

  const fileTypes: CustomerCustomAttachment['file_type'][] = [
    'pdf', 'doc', 'docx', 'xlsx', 'xls', 'txt', 'jpg', 'jpeg', 'png', 'xml', 'zip'
  ];

  if (loading) {
    return <div style={{ padding: '20px', textAlign: 'center' }}>Lade Anhänge...</div>;
  }

  return (
    <div>
      {/* Header mit Add Button */}
      <div style={{ 
        display: 'flex', 
        justifyContent: 'space-between', 
        alignItems: 'center',
        marginBottom: '20px'
      }}>
        <div>
          <h3 style={{ margin: 0, fontSize: '16px', fontWeight: '600' }}>
            Individuelle Anhänge
          </h3>
          <p style={{ margin: '4px 0 0 0', fontSize: '14px', color: '#6B7280' }}>
            Diese Anhänge werden bei jedem Upload für diesen Kunden angefordert
          </p>
        </div>
        {!isAdding && (
          <button
            onClick={() => setIsAdding(true)}
            style={{
              display: 'flex',
              alignItems: 'center',
              gap: '6px',
              padding: '8px 16px',
              backgroundColor: '#3B82F6',
              color: 'white',
              border: 'none',
              borderRadius: '6px',
              cursor: 'pointer',
              fontSize: '14px',
              fontWeight: '500'
            }}
          >
            <Plus size={16} />
            Anhang hinzufügen
          </button>
        )}
      </div>

      {/* Add Form */}
      {isAdding && (
        <div style={{
          padding: '16px',
          backgroundColor: '#F9FAFB',
          borderRadius: '8px',
          marginBottom: '16px',
          border: '1px solid #E5E7EB'
        }}>
          <div style={{ display: 'grid', gap: '12px' }}>
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '12px' }}>
              <div>
                <label style={{ 
                  display: 'block', 
                  marginBottom: '4px', 
                  fontSize: '14px', 
                  fontWeight: '500' 
                }}>
                  Dateiname *
                </label>
                <input
                  type="text"
                  value={newForm.filename}
                  onChange={(e) => setNewForm({ ...newForm, filename: e.target.value })}
                  placeholder="z.B. Stundenzettel"
                  style={{
                    width: '100%',
                    padding: '8px 12px',
                    border: '1px solid #D1D5DB',
                    borderRadius: '6px',
                    fontSize: '14px'
                  }}
                />
              </div>
              
              <div>
                <label style={{ 
                  display: 'block', 
                  marginBottom: '4px', 
                  fontSize: '14px', 
                  fontWeight: '500' 
                }}>
                  Dateityp *
                </label>
                <select
                  value={newForm.file_type}
                  onChange={(e) => setNewForm({ 
                    ...newForm, 
                    file_type: e.target.value as CustomerCustomAttachment['file_type'] 
                  })}
                  style={{
                    width: '100%',
                    padding: '8px 12px',
                    border: '1px solid #D1D5DB',
                    borderRadius: '6px',
                    fontSize: '14px'
                  }}
                >
                  {fileTypes.map(type => (
                    <option key={type} value={type}>
                      {getFileTypeLabel(type)}
                    </option>
                  ))}
                </select>
              </div>
            </div>

            <div>
              <label style={{ 
                display: 'block', 
                marginBottom: '4px', 
                fontSize: '14px', 
                fontWeight: '500' 
              }}>
                Beschreibung
              </label>
              <input
                type="text"
                value={newForm.description}
                onChange={(e) => setNewForm({ ...newForm, description: e.target.value })}
                placeholder="Optionale Beschreibung"
                style={{
                  width: '100%',
                  padding: '8px 12px',
                  border: '1px solid #D1D5DB',
                  borderRadius: '6px',
                  fontSize: '14px'
                }}
              />
            </div>

            <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
              <input
                type="checkbox"
                id="new-required"
                checked={newForm.is_required}
                onChange={(e) => setNewForm({ ...newForm, is_required: e.target.checked })}
                style={{ cursor: 'pointer' }}
              />
              <label htmlFor="new-required" style={{ fontSize: '14px', cursor: 'pointer' }}>
                Erforderlich (muss hochgeladen werden)
              </label>
            </div>

            <div style={{ display: 'flex', gap: '8px', justifyContent: 'flex-end' }}>
              <button
                onClick={() => {
                  setIsAdding(false);
                  setNewForm({
                    filename: '',
                    description: '',
                    file_type: 'pdf',
                    is_required: true
                  });
                }}
                style={{
                  padding: '8px 16px',
                  backgroundColor: '#F3F4F6',
                  color: '#374151',
                  border: 'none',
                  borderRadius: '6px',
                  cursor: 'pointer',
                  fontSize: '14px'
                }}
              >
                Abbrechen
              </button>
              <button
                onClick={handleAdd}
                disabled={!newForm.filename.trim()}
                style={{
                  padding: '8px 16px',
                  backgroundColor: newForm.filename.trim() ? '#10B981' : '#9CA3AF',
                  color: 'white',
                  border: 'none',
                  borderRadius: '6px',
                  cursor: newForm.filename.trim() ? 'pointer' : 'not-allowed',
                  fontSize: '14px'
                }}
              >
                Hinzufügen
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Attachments Liste */}
      {attachments.length === 0 ? (
        <div style={{
          padding: '40px',
          textAlign: 'center',
          backgroundColor: '#F9FAFB',
          borderRadius: '8px',
          border: '1px solid #E5E7EB'
        }}>
          <FileText size={48} color="#9CA3AF" style={{ marginBottom: '12px' }} />
          <p style={{ color: '#6B7280', margin: 0 }}>
            Keine individuellen Anhänge definiert
          </p>
        </div>
      ) : (
        <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
          {attachments.map((attachment, index) => (
            <div
              key={attachment.id}
              style={{
                padding: '12px',
                backgroundColor: 'white',
                border: '1px solid #E5E7EB',
                borderRadius: '6px',
                display: 'flex',
                alignItems: 'center',
                gap: '12px'
              }}
            >
              {/* Drag Handle */}
              <div style={{ 
                display: 'flex', 
                flexDirection: 'column', 
                gap: '4px' 
              }}>
                <button
                  onClick={() => handleMoveUp(index)}
                  disabled={index === 0}
                  style={{
                    padding: '2px',
                    backgroundColor: 'transparent',
                    border: 'none',
                    cursor: index === 0 ? 'not-allowed' : 'pointer',
                    opacity: index === 0 ? 0.3 : 1
                  }}
                >
                  ↑
                </button>
                <GripVertical size={16} color="#9CA3AF" />
                <button
                  onClick={() => handleMoveDown(index)}
                  disabled={index === attachments.length - 1}
                  style={{
                    padding: '2px',
                    backgroundColor: 'transparent',
                    border: 'none',
                    cursor: index === attachments.length - 1 ? 'not-allowed' : 'pointer',
                    opacity: index === attachments.length - 1 ? 0.3 : 1
                  }}
                >
                  ↓
                </button>
              </div>

              {/* Content */}
              <div style={{ flex: 1 }}>
                {editingId === attachment.id ? (
                  <div style={{ display: 'grid', gap: '8px' }}>
                    <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '8px' }}>
                      <input
                        type="text"
                        value={editForm.filename}
                        onChange={(e) => setEditForm({ ...editForm, filename: e.target.value })}
                        style={{
                          padding: '6px 10px',
                          border: '1px solid #D1D5DB',
                          borderRadius: '4px',
                          fontSize: '14px'
                        }}
                      />
                      <select
                        value={editForm.file_type}
                        onChange={(e) => setEditForm({ 
                          ...editForm, 
                          file_type: e.target.value as CustomerCustomAttachment['file_type'] 
                        })}
                        style={{
                          padding: '6px 10px',
                          border: '1px solid #D1D5DB',
                          borderRadius: '4px',
                          fontSize: '14px'
                        }}
                      >
                        {fileTypes.map(type => (
                          <option key={type} value={type}>
                            {getFileTypeLabel(type)}
                          </option>
                        ))}
                      </select>
                    </div>
                    <input
                      type="text"
                      value={editForm.description}
                      onChange={(e) => setEditForm({ ...editForm, description: e.target.value })}
                      placeholder="Beschreibung"
                      style={{
                        padding: '6px 10px',
                        border: '1px solid #D1D5DB',
                        borderRadius: '4px',
                        fontSize: '14px'
                      }}
                    />
                    <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                      <input
                        type="checkbox"
                        id={`required-${attachment.id}`}
                        checked={editForm.is_required}
                        onChange={(e) => setEditForm({ ...editForm, is_required: e.target.checked })}
                      />
                      <label htmlFor={`required-${attachment.id}`} style={{ fontSize: '14px' }}>
                        Erforderlich
                      </label>
                    </div>
                  </div>
                ) : (
                  <>
                    <div style={{ 
                      fontWeight: '500', 
                      fontSize: '14px',
                      display: 'flex',
                      alignItems: 'center',
                      gap: '8px'
                    }}>
                      {attachment.filename}
                      <span style={{
                        padding: '2px 6px',
                        backgroundColor: '#E0E7FF',
                        color: '#3730A3',
                        borderRadius: '4px',
                        fontSize: '12px'
                      }}>
                        {getFileTypeLabel(attachment.file_type)}
                      </span>
                      {attachment.is_required && (
                        <span style={{
                          padding: '2px 6px',
                          backgroundColor: '#FEE2E2',
                          color: '#991B1B',
                          borderRadius: '4px',
                          fontSize: '12px'
                        }}>
                          Erforderlich
                        </span>
                      )}
                    </div>
                    {attachment.description && (
                      <div style={{ fontSize: '13px', color: '#6B7280', marginTop: '4px' }}>
                        {attachment.description}
                      </div>
                    )}
                  </>
                )}
              </div>

              {/* Actions */}
              <div style={{ display: 'flex', gap: '8px' }}>
                {editingId === attachment.id ? (
                  <>
                    <button
                      onClick={() => handleSaveEdit(attachment.id)}
                      style={{
                        padding: '6px',
                        backgroundColor: '#10B981',
                        color: 'white',
                        border: 'none',
                        borderRadius: '4px',
                        cursor: 'pointer'
                      }}
                    >
                      <Save size={16} />
                    </button>
                    <button
                      onClick={() => setEditingId(null)}
                      style={{
                        padding: '6px',
                        backgroundColor: '#F3F4F6',
                        color: '#374151',
                        border: 'none',
                        borderRadius: '4px',
                        cursor: 'pointer'
                      }}
                    >
                      <X size={16} />
                    </button>
                  </>
                ) : (
                  <>
                    <button
                      onClick={() => handleEdit(attachment)}
                      style={{
                        padding: '6px',
                        backgroundColor: '#F3F4F6',
                        color: '#374151',
                        border: 'none',
                        borderRadius: '4px',
                        cursor: 'pointer'
                      }}
                    >
                      <Edit2 size={16} />
                    </button>
                    <button
                      onClick={() => handleDelete(attachment.id)}
                      style={{
                        padding: '6px',
                        backgroundColor: '#FEE2E2',
                        color: '#991B1B',
                        border: 'none',
                        borderRadius: '4px',
                        cursor: 'pointer'
                      }}
                    >
                      <Trash2 size={16} />
                    </button>
                  </>
                )}
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};